import { useTranslation } from 'react-i18next';

import type { AppointmentInfo as AppointmentInfoData } from '../token-module/appointment-info.interface.js';

interface AppointmentInfoProps {
  readonly info?: AppointmentInfoData;
  /** Optional anders gesetzte Headline (Default: `start.headline`). */
  readonly headlineKey?: string;
}

/**
 * Zentrierte Termininformation für das Lovable-Layout: Headline + Datum
 * + Standort. Ersetzt visuell die Card-Box der bisherigen `AppointmentCard`
 * — die ungebundene Variante (kein `info`) fällt auf die i18n-Platzhalter
 * zurück, damit sie auch im Mock-Switcher ohne Token-Routing rendert.
 *
 * Datums-Label (N-4-Quickfix): Der Cancel-Link trägt den Zeitslot aus der
 * SMS; wird der Termin in der Praxis verschoben, zeigt der Link weiterhin den
 * alten Slot. Das Label „Ursprünglich vereinbart für:" macht aus der Anzeige
 * eine wahre Aussage statt einer falschen Behauptung. Es erscheint nur mit
 * echter Termin-Info (nicht beim Mock-Platzhalter). Wortlaut ist als offen
 * markiert (Abstimmung Linda); die Link-Ersetzung bei Verschiebung ist als
 * eigenes Feature nach 72.0 geplant.
 *
 * Anzeige-Logik des Standorts:
 *  1. Kein `info` (Mock/ungebunden) → `start.appointmentLocation` Platzhalter.
 *  2. `info.locationName` da         → Name direkt.
 *  3. Sonst (kein Mapping)           → gar keine Standort-Zeile. Eine rohe
 *     „Standort {{id}}"-Anzeige würde Patient:innen nur verwirren — lieber
 *     nichts als eine bedeutungslose ID.
 */
export function AppointmentInfo(props: AppointmentInfoProps): JSX.Element {
  const { t } = useTranslation();
  const summary = props.info?.formattedDateTime ?? t('start.appointmentSummary');
  const locationText =
    props.info === undefined ? t('start.appointmentLocation') : props.info.locationName;
  const headlineKey = props.headlineKey ?? 'start.headline';
  return (
    <section
      aria-label={t('appointment.heading')}
      className="flex flex-col items-center gap-3 text-center"
    >
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t(headlineKey)}</h1>
      {props.info ? (
        <p className="text-sm text-muted-foreground" data-testid="appointment-date-label">
          {t('appointment.originallyScheduledFor')}
        </p>
      ) : null}
      <p className="text-base font-medium text-foreground" data-testid="appointment-summary">
        {summary}
      </p>
      {locationText !== undefined ? (
        <p className="text-sm text-brand" data-testid="appointment-location">
          ({locationText})
        </p>
      ) : null}
      {props.info ? (
        <p className="sr-only" data-testid="appointment-id">
          {props.info.appointmentId}
        </p>
      ) : null}
    </section>
  );
}
